import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { Currency } from './currency.interface';
import { ExchangeRate } from './exchange-rate.interface';
import { exchange } from './exchange';
import { NgxPermissionsService } from 'ngx-permissions';
import { logger } from '@icc/shared/helpers';

@Injectable({
    providedIn: 'root',
})
export class ExchangeRateService {

    currencies: Currency[] = [];
    exchangeRates: ExchangeRate[] = [];
    private salesCurrencies: Currency[] = [];
    private salesExchangeRates: ExchangeRate[] = [];
    buyCurrencies: Currency[] = [];
    buyExchangeRates: ExchangeRate[] = [];
    salesCurrenciesFromBuy = false;
    canChangeSaleCurrency = false;

    currenciesMap: Record<Currency['code'], Currency> = {};

    loaded: Promise<boolean>;
    _loaded = false;
    private resolve: (value: boolean) => void;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private permissionsService: NgxPermissionsService
    ) {
        this.loaded = new Promise(resolve => {
            this.resolve = resolve;
        });
    }

    public async loadRates() {
        if (!this.currencies?.length) {
            const data = await this.http.get<{
                currencies: Currency[],
                exchangeRates: ExchangeRate[],
                buyCurrencies: Currency[],
                buyExchangeRates: ExchangeRate[],
                salesCurrenciesFromBuy: boolean,
            }>(`${window.location.origin}/api/b2b/exchange-rates`).toPromise();
            this.buyCurrencies = data.buyCurrencies;
            this.buyExchangeRates = data.buyExchangeRates;
            this.salesCurrenciesFromBuy = data.salesCurrenciesFromBuy;
            this.currencies = data.currencies;
            this.exchangeRates = data.exchangeRates;
            this.salesCurrencies = this.currencies;
            this.salesExchangeRates = this.exchangeRates;
        }
        this.canChangeSaleCurrency = await this.permissionsService.hasPermission(['trade-change-sale-currency']);
        if (!this.canChangeSaleCurrency) {
            this.exchangeRates = this.buyExchangeRates;
            this.currencies = this.buyCurrencies;
        } else {
            this.exchangeRates = this.salesExchangeRates;
            this.currencies = this.salesCurrencies;
        }
        this._loaded = true;
        this.resolve(true);
        logger.log('loaded exchange rates');
    }

    public getRates() {
        return this.exchangeRates;
    }

    public getBuyRates() {
        return this.buyExchangeRates;
    }

    public getMainCurrency() {
        if (this.configService.user?.user?.ActiveCompanyDefaultCurrency) {
            const currency = this.getCurrency(this.configService.user?.user?.ActiveCompanyDefaultCurrency);
            if (currency) {
                return currency;
            }
        }
        if (this.configService.user?.user?.ActiveMarketDefaultCurrency) {
            const currency = this.getCurrency(this.configService.user?.user?.ActiveMarketDefaultCurrency);
            if (currency) {
                return currency;
            }
        }
        return this.currencies[0];
    }

    public getBuyCurrency() {
        if (this.configService.user?.user?.ActiveMarketDefaultCurrency) {
            const currency = this.getCurrency(this.configService.user?.user?.ActiveMarketDefaultCurrency, true);
            if (currency) {
                return currency;
            }
        }
        return this.buyCurrencies[0];
    }

    public getCurrency(code: string, buy = false) {
        if (this.currenciesMap[code]) {
            return this.currenciesMap[code];
        }
        const currency = (buy ? this.buyCurrencies : this.currencies).find(c => c.code === code);
        if (currency) {
            this.currenciesMap[code] = currency;
        }
        return this.currenciesMap[code];
    }

    public getCurrencySymbol(code: string) {
        const currency = this.getCurrency(code);
        if (currency.symbol) {
            return currency.symbol;
        }
        const formatter = Intl.NumberFormat(this.configService.config.languageCode, {
            currency: currency.code,
            style: 'currency'
        });
        const parts = formatter.formatToParts(1);
        return parts.find(el => el.type === 'currency')?.value;
    }

    public exchange(value: number, from: string, to: string, buy = false) {
        return exchange(value, from, to, buy ? this.buyExchangeRates : this.exchangeRates);
    }
}
